import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export type Lang = 'fr' | 'en';

const translations = {
  fr: {
    // Home
    'home.createRoom': 'Créer une salle',
    'home.creating': 'Création…',
    'home.or': 'ou',
    'home.haveLink': 'Vous avez un lien ? Entrez le code',
    'home.enterCode': 'Code de la salle :',
    'home.codePlaceholder': 'ex. 8f72a9…',
    'home.join': 'Rejoindre',
    'home.whoAreYou': 'Qui êtes-vous ?',
    'home.nameDesc': 'Entrez un nom pour rejoindre. Aucun compte, aucun suivi.',
    'home.namePlaceholder': 'Prénom ou pseudonyme',
    'home.joinRoom': 'Rejoindre la salle',
    // Room
    'room.notFound': 'Salle introuvable',
    'room.notFoundDesc': "Cette salle a peut-être été fermée ou n'existe pas.",
    'room.linkCopied': 'Lien copié dans le presse-papiers',
    'room.copyFailed': 'Impossible de copier le lien.',
    'room.loading': 'Chargement…',
    'room.copyLink': 'Copier le lien',
    'room.leave': 'Quitter',
    'room.enterName': 'Entrez votre nom',
    'room.needName': 'Vous avez besoin d\'un nom pour rejoindre cette salle.',
    'room.joinRoom': 'Rejoindre',
    // Chat window
    'chat.reconnecting': 'Connexion perdue. Reconnexion…',
    'chat.roomClosed': 'Salle fermée',
    'chat.roomClosedDesc': 'Cette salle a été fermée. Tous les messages et participants ont été définitivement supprimés.',
    'chat.goHome': 'Retour à l\'accueil',
    'chat.quiet': 'C\'est calme ici.',
    'chat.noMessages': 'Les messages ne sont pas sauvegardés. Ils disparaissent quand vous partez.',
    // Chat input
    'chat.placeholder': 'Écrire un message…',
    'chat.send': 'Envoyer',
    'chat.cancelDm': 'Annuler le message privé',
    'chat.privateLabel': 'Message privé à',
    // Sidebar
    'sidebar.inRoom': 'Dans la salle',
    'sidebar.you': 'Vous',
    'sidebar.gathering': 'Participants en attente…',
    'sidebar.footer': 'Quand la salle se ferme, tout disparaît pour toujours.',
    // Typing
    'typing.one': 'écrit…',
    'typing.two': 'et',
    'typing.twoSuffix': 'écrivent…',
    'typing.several': 'Plusieurs personnes écrivent…',
    // System messages
    'system.joined': 'a rejoint',
    'system.left': 'a quitté',
    // Private message labels
    'pm.you': 'vous',
    'pm.private': 'privé',
    'common.error': 'Erreur',
  },
  en: {
    // Home
    'home.createRoom': 'Create a room',
    'home.creating': 'Creating…',
    'home.or': 'or',
    'home.haveLink': 'Have a link? Enter room code',
    'home.enterCode': 'Enter room code:',
    'home.codePlaceholder': 'e.g. 8f72a9…',
    'home.join': 'Join',
    'home.whoAreYou': 'Who are you?',
    'home.nameDesc': 'Enter a name to join the room. No accounts, no tracking.',
    'home.namePlaceholder': 'First name or pseudonym',
    'home.joinRoom': 'Join Room',
    // Room
    'room.notFound': 'Room not found',
    'room.notFoundDesc': "This room might have been closed or doesn't exist.",
    'room.linkCopied': 'Room link copied to clipboard',
    'room.copyFailed': 'Unable to copy the link.',
    'room.loading': 'Loading room…',
    'room.copyLink': 'Copy link',
    'room.leave': 'Leave',
    'room.enterName': 'Enter your name',
    'room.needName': 'You need a name to join this room.',
    'room.joinRoom': 'Join Room',
    // Chat window
    'chat.reconnecting': 'Connection lost. Reconnecting…',
    'chat.roomClosed': 'Room Closed',
    'chat.roomClosedDesc': 'This room has been closed. All messages and participants have been permanently removed.',
    'chat.goHome': 'Go home',
    'chat.quiet': "It's quiet here.",
    'chat.noMessages': 'Messages are not saved. They disappear when you leave.',
    // Chat input
    'chat.placeholder': 'Type a message…',
    'chat.send': 'Send',
    'chat.cancelDm': 'Cancel private message',
    'chat.privateLabel': 'Private message to',
    // Sidebar
    'sidebar.inRoom': 'In the room',
    'sidebar.you': 'You',
    'sidebar.gathering': 'Gathering participants…',
    'sidebar.footer': 'When the room closes, everything disappears forever.',
    // Typing
    'typing.one': 'is typing…',
    'typing.two': 'and',
    'typing.twoSuffix': 'are typing…',
    'typing.several': 'Several people are typing…',
    // System messages
    'system.joined': 'joined',
    'system.left': 'left',
    // Private message labels
    'pm.you': 'you',
    'pm.private': 'private',
    'common.error': 'Error',
  },
} as const;

type TranslationKey = keyof typeof translations.fr;

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TranslationKey) => string;
}

const LangContext = createContext<LangContextValue | null>(null);

function getInitialLang(): Lang {
  try {
    const stored = localStorage.getItem('tobius-lang');
    if (stored === 'fr' || stored === 'en') return stored;
    if (navigator.language.startsWith('fr')) return 'fr';
  } catch { /* ignore */ }
  return 'en';
}

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try { localStorage.setItem('tobius-lang', l); } catch { /* ignore */ }
  }, []);

  const t = useCallback((key: TranslationKey): string => {
    return translations[lang][key] as string;
  }, [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used inside LangProvider');
  return ctx;
}
